/**
 * A very small ZIP writer.
 *
 * CLAUDE.md says not to add dependencies without asking, and a zip library is
 * a lot of code to carry for one screen. This writes the "stored" variant —
 * entries are not compressed — which is about eighty lines and costs nothing
 * in size here, because a month of exports is PDFs and JPEGs and those are
 * already compressed. Deflating them again saves single-digit percentages for
 * a large library and a new failure mode.
 *
 * Everything is the classic PKZIP layout: a local header before each file, a
 * central directory afterwards, and an end record pointing at it. No ZIP64, no
 * encryption, no streaming — a family's month of paperwork is tens of
 * megabytes, not gigabytes.
 */

export interface ZipEntry {
  name: string
  bytes: Uint8Array
}

/**
 * TypeScript now types Uint8Array by its backing buffer, and a SharedArrayBuffer
 * cannot go into a Blob. Everything here is an ordinary array, so this narrows
 * the type without copying a byte.
 */
type BlobSafe = Uint8Array<ArrayBuffer>

const CRC_TABLE = buildCrcTable()

function buildCrcTable(): Uint32Array {
  const table = new Uint32Array(256)
  for (let i = 0; i < 256; i++) {
    let c = i
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1
    table[i] = c >>> 0
  }
  return table
}

export function crc32(bytes: Uint8Array): number {
  let c = 0xffffffff
  for (let i = 0; i < bytes.length; i++) c = CRC_TABLE[(c ^ bytes[i]!) & 0xff]! ^ (c >>> 8)
  return (c ^ 0xffffffff) >>> 0
}

export function buildZip(entries: ZipEntry[]): Blob {
  const encoder = new TextEncoder()
  const parts: BlobPart[] = []
  const central: BlobSafe[] = []
  let offset = 0

  for (const entry of entries) {
    const nameBytes = encoder.encode(entry.name)
    const crc = crc32(entry.bytes)
    const size = entry.bytes.length

    const local = new Uint8Array(30 + nameBytes.length)
    const view = new DataView(local.buffer)
    view.setUint32(0, 0x04034b50, true)
    view.setUint16(4, 20, true) // version needed
    // Bit 11: names are UTF-8. Without it, "Elektriciteit café.pdf" unzips
    // with mojibake on Windows.
    view.setUint16(6, 0x0800, true)
    view.setUint16(8, 0, true) // stored, no compression
    view.setUint16(10, 0, true) // time
    view.setUint16(12, 0, true) // date
    view.setUint32(14, crc, true)
    view.setUint32(18, size, true)
    view.setUint32(22, size, true)
    view.setUint16(26, nameBytes.length, true)
    view.setUint16(28, 0, true)
    local.set(nameBytes, 30)

    parts.push(local, entry.bytes as BlobSafe)

    const dir = new Uint8Array(46 + nameBytes.length)
    const dv = new DataView(dir.buffer)
    dv.setUint32(0, 0x02014b50, true)
    dv.setUint16(4, 20, true)
    dv.setUint16(6, 20, true)
    dv.setUint16(8, 0x0800, true)
    dv.setUint16(10, 0, true)
    dv.setUint16(12, 0, true)
    dv.setUint16(14, 0, true)
    dv.setUint32(16, crc, true)
    dv.setUint32(20, size, true)
    dv.setUint32(24, size, true)
    dv.setUint16(28, nameBytes.length, true)
    dv.setUint32(42, offset, true)
    dir.set(nameBytes, 46)
    central.push(dir)

    offset += local.length + size
  }

  const centralSize = central.reduce((sum, d) => sum + d.length, 0)
  const end = new Uint8Array(22)
  const ev = new DataView(end.buffer)
  ev.setUint32(0, 0x06054b50, true)
  ev.setUint16(8, entries.length, true)
  ev.setUint16(10, entries.length, true)
  ev.setUint32(12, centralSize, true)
  ev.setUint32(16, offset, true)

  return new Blob([...parts, ...central, end], { type: 'application/zip' })
}

/**
 * A filename you can recognise in a folder listing a year from now.
 *
 * Date first so a plain alphabetical sort is chronological, then who it was
 * from, then what it cost. Anything a filesystem dislikes is replaced rather
 * than dropped, so two invoices never collapse onto one name.
 */
export function exportFilename(params: {
  date: string
  supplier: string
  amountCents: number
  extension: string
}): string {
  const supplier = params.supplier
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .slice(0, 40)
  const euros = (params.amountCents / 100).toFixed(2).replace('.', ',')
  return `${params.date}_${supplier || 'onbekend'}_${euros}.${params.extension}`
}

/** RFC 4180: quote everything, double the quotes inside. Excel is unforgiving. */
export function toCsv(rows: (string | number | null)[][]): string {
  return rows
    .map((row) =>
      row
        .map((cell) => `"${String(cell ?? '').replace(/"/g, '""')}"`)
        .join(';'),
    )
    .join('\r\n')
}
